import { lookupTerm, REFERENCE_TERMS } from '../vocabulary/index.js';
import { INTEROP_NODE_TYPES, type InteropNodeType } from './interop.js';

/**
 * The rule catalogue an interchange export is held to, checked against the document rather
 * than trusted from the serialiser.
 *
 * The processors this dialect exists for are not JSON-LD engines. They do not expand the
 * document; they read the raw JSON keys, and they answer anything else with
 *
 *   > Unsupported JSON-LD graph shape. Expected "@context" and "@graph", with graph items
 *   > using "id", "type", and supported types such as Entity, Actor, User, Role.
 *
 * So "valid JSON-LD" is not the bar — a *normalised shape* is, and the shape is only knowable
 * from the bytes. `serialiseInteropGraph` produces it; this module is the independent second
 * opinion, so a change to the serialiser, a new ontology term or an author-typed id that
 * happens not to be a CURIE cannot ship a document a processor will refuse.
 *
 * Rule ids (`R1.1`, `R5.4`, …) are the ones in `DPEM-JSONLD-Compliance-Rules.md` and are
 * stable: a violation is quoted with its id, and §12's JSON Schema is implemented here rather
 * than carried as a schema file so the three checks that schema cannot express — the
 * stringified-array content of list attributes (R5.4), referential integrity (R8.1–R8.3) and
 * undeclared prefixes (R2.3) — live next to the ones it can.
 *
 * `error` versus `warning` is not a judgement about tidiness. An error is a shape the
 * processor rejects. A warning is a shape it accepts and a human would still want to know
 * about — a dangling reference is the example, and it is a warning rather than an error
 * because the accepted reference export carries one: a variant's `variantOf` names a parent a
 * single-method export does not contain, and dropping that edge would misreport the method as
 * having no parent.
 */

export type ComplianceSeverity = 'error' | 'warning';

export interface ComplianceViolation {
  /** Rule id from the compliance rules document, e.g. `R5.3`. */
  readonly rule: string;
  readonly severity: ComplianceSeverity;
  /** Dotted path into the document, e.g. `@graph[7].attributes.order`. */
  readonly path: string;
  readonly message: string;
}

export interface ComplianceReport {
  readonly violations: readonly ComplianceViolation[];
  readonly errorCount: number;
  readonly warningCount: number;
  /** True when nothing in the document is a shape the processor rejects. */
  readonly compliant: boolean;
}

/** R4.5/R7.1/R7.4: `prefix:local`, with a local part safe in an identifier. */
const CURIE = /^[A-Za-z][\w.-]*:[\w.-]+$/;

/** R1.1 */
const DOCUMENT_KEYS: readonly string[] = ['@context', '@graph'];

/** R4.2, in the order R4 requires them to be written. */
const NODE_KEYS: readonly string[] = [
  'id',
  'type',
  'name',
  'description',
  'relatesTo',
  'attributes',
];

/** R4.3 */
const REQUIRED_NODE_KEYS: readonly string[] = ['id', 'type', 'name', 'attributes'];

/** R2.2 */
const REQUIRED_CONTEXT_TERMS: readonly string[] = [
  'name',
  'description',
  'attributes',
  'relatesTo',
];

/** R5.7: these are node-level keys and mean something else inside `attributes`. */
const FORBIDDEN_ATTRIBUTE_KEYS: readonly string[] = [
  'id',
  'type',
  'name',
  'description',
  'relatesTo',
];

/** R2.4: keys whose presence would mean the shape depends on expansion. */
const CONTEXT_KEYS_THAT_IMPLY_EXPANSION: readonly string[] = ['@vocab', '@base'];

const REFERENCE_TERM_NAMES: ReadonlySet<string> = new Set(
  REFERENCE_TERMS.map((term) => term.term),
);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function prefixOf(curie: string): string | undefined {
  const separator = curie.indexOf(':');
  return separator > 0 ? curie.slice(0, separator) : undefined;
}

/**
 * Every rule the document breaks, in document order.
 *
 * Takes `unknown` on purpose: the thing worth checking is a file — an export about to be
 * written, a download a user is asking about — not an in-memory type that is correct by
 * construction.
 */
export function checkInteropCompliance(document: unknown): readonly ComplianceViolation[] {
  const violations: ComplianceViolation[] = [];
  const error = (rule: string, path: string, message: string): void => {
    violations.push({ rule, severity: 'error', path, message });
  };
  const warn = (rule: string, path: string, message: string): void => {
    violations.push({ rule, severity: 'warning', path, message });
  };

  if (!isPlainObject(document)) {
    error('R1.1', '', 'the document must be a single JSON object');
    return violations;
  }

  const keys = Object.keys(document);
  for (const key of keys) {
    if (!DOCUMENT_KEYS.includes(key)) {
      error('R1.1', key, `unexpected top-level key "${key}"; only @context and @graph are allowed`);
    }
  }

  const context = document['@context'];
  const declaredPrefixes = checkContext(context, error, warn);

  const graph = document['@graph'];
  if (!Array.isArray(graph)) {
    error('R1.4', '@graph', '@graph must be an array of node objects');
    return violations;
  }
  if (graph.length === 0) error('R1.2', '@graph', '@graph must not be empty');

  const ids = new Set<string>();
  /** CURIE → first path that referenced it, for the R8.1 pass. */
  const references = new Map<string, string>();

  for (let index = 0; index < graph.length; index += 1) {
    const path = `@graph[${String(index)}]`;
    checkNode(graph[index], path, { declaredPrefixes, ids, references, error, warn });
  }

  for (const [reference, path] of references) {
    if (!ids.has(reference)) {
      warn('R8.1', path, `"${reference}" is not the id of any node in this @graph`);
    }
  }

  return violations;
}

type Report = (rule: string, path: string, message: string) => void;

interface NodeContext {
  readonly declaredPrefixes: ReadonlySet<string>;
  readonly ids: Set<string>;
  readonly references: Map<string, string>;
  readonly error: Report;
  readonly warn: Report;
}

/** R2.1–R2.4. Returns the prefixes the context declares, for R2.3. */
function checkContext(context: unknown, error: Report, warn: Report): ReadonlySet<string> {
  if (!isPlainObject(context)) {
    error('R1.1', '@context', '@context must be an object');
    return new Set();
  }

  if (context.id !== '@id') error('R2.1', '@context.id', 'must alias "@id"');
  if (context.type !== '@type') error('R2.1', '@context.type', 'must alias "@type"');
  for (const term of REQUIRED_CONTEXT_TERMS) {
    if (context[term] === undefined) error('R2.2', `@context.${term}`, 'must be defined');
  }
  for (const key of CONTEXT_KEYS_THAT_IMPLY_EXPANSION) {
    if (context[key] !== undefined) {
      warn('R2.4', `@context.${key}`, 'the processor does not expand, so this has no effect');
    }
  }

  // A prefix declaration is a bare IRI ending in a delimiter; a term definition is not. Both
  // are strings, so the value is what tells them apart.
  const prefixes = new Set<string>();
  for (const [key, value] of Object.entries(context)) {
    if (typeof value === 'string' && /[#/:\-_]$/.test(value)) prefixes.add(key);
  }
  return prefixes;
}

/** R3.x, R4.x, R5.x, R6.x, R7.x for one node. */
function checkNode(node: unknown, path: string, context: NodeContext): void {
  const { declaredPrefixes, ids, references, error, warn } = context;

  if (!isPlainObject(node)) {
    error('R1.2', path, 'every @graph item must be an object');
    return;
  }

  for (const key of Object.keys(node)) {
    if (key === '@id' || key === '@type') {
      error('R10.1', `${path}.${key}`, `use "${key.slice(1)}" instead of "${key}"`);
      continue;
    }
    if (!NODE_KEYS.includes(key)) {
      error(
        'R4.2',
        `${path}.${key}`,
        `"${key}" is not an allowed node key; a domain property belongs in attributes`,
      );
    }
  }
  for (const key of REQUIRED_NODE_KEYS) {
    if (node[key] === undefined) error('R4.3', `${path}.${key}`, 'is required on every node');
  }

  const id = node.id;
  if (typeof id === 'string' && id.length > 0) {
    if (!CURIE.test(id)) {
      error('R4.5', `${path}.id`, `"${id}" is not a CURIE of the form prefix:localName`);
    } else {
      const prefix = prefixOf(id) as string;
      if (!declaredPrefixes.has(prefix)) {
        error('R2.3', `${path}.id`, `prefix "${prefix}" is not declared in @context`);
      }
    }
    if (ids.has(id)) error('R8.3', `${path}.id`, `"${id}" is used by more than one node`);
    ids.add(id);
  } else if (id !== undefined) {
    error('R4.5', `${path}.id`, 'must be a non-empty string');
  }

  const type = node.type;
  if (Array.isArray(type)) {
    error('R3.6', `${path}.type`, 'must be a single string, never an array');
  } else if (type !== undefined && !INTEROP_NODE_TYPES.includes(type as InteropNodeType)) {
    error(
      'R3.1',
      `${path}.type`,
      `"${String(type)}" is not a supported type; use one of ${INTEROP_NODE_TYPES.join(', ')} and put the ontology class in attributes.dpemType`,
    );
  }

  if (node.name !== undefined && (typeof node.name !== 'string' || node.name.length === 0)) {
    error('R4.6', `${path}.name`, 'must be a non-empty string');
  }
  if (node.description !== undefined && typeof node.description !== 'string') {
    error('R4.7', `${path}.description`, 'must be a string when present');
  }

  checkAttributes(node.attributes, `${path}.attributes`, context);
  checkRelatesTo(node.relatesTo, `${path}.relatesTo`, context);

  // Referenced prefixes are checked here rather than in the R8.1 pass because only the node
  // knows which of its attribute values are references at all: an arbitrary string may hold a
  // colon, and a `writeup` that opens with a markdown link is not an undeclared prefix.
  for (const [reference, referencePath] of collectReferences(node, path)) {
    if (!CURIE.test(reference)) {
      warn('R7.1', referencePath, `"${reference}" is not a CURIE`);
      continue;
    }
    const prefix = prefixOf(reference) as string;
    if (!declaredPrefixes.has(prefix)) {
      error('R2.3', referencePath, `prefix "${prefix}" is not declared in @context`);
    }
    if (!references.has(reference)) references.set(reference, referencePath);
  }
}

/** R5.1–R5.7, plus the colon ban a loader taught us. */
function checkAttributes(attributes: unknown, path: string, context: NodeContext): void {
  const { declaredPrefixes, error, warn } = context;

  if (attributes === undefined) return;
  if (!isPlainObject(attributes)) {
    error('R5.1', path, 'must be a JSON object, not a string or an array');
    return;
  }

  const dpemType = attributes.dpemType;
  if (typeof dpemType !== 'string' || dpemType.length === 0) {
    error('R5.2', `${path}.dpemType`, 'is required and must be the node’s dpem: class');
  } else if (!CURIE.test(dpemType)) {
    error('R5.2', `${path}.dpemType`, `"${dpemType}" is not a CURIE`);
  } else if (!declaredPrefixes.has(prefixOf(dpemType) as string)) {
    error('R2.3', `${path}.dpemType`, `prefix "${String(prefixOf(dpemType))}" is not declared in @context`);
  }

  for (const [key, value] of Object.entries(attributes)) {
    const valuePath = `${path}.${key}`;
    if (FORBIDDEN_ATTRIBUTE_KEYS.includes(key)) {
      error('R5.7', valuePath, `"${key}" belongs at the node top level, not in attributes`);
    }
    if (key.includes(':')) {
      // A tokeniser reads `relatesTo:hasStage` as a compact IRI whose prefix is a term rather
      // than a prefix, and refuses the whole document with "invalid token".
      error('R5.6', valuePath, `attribute key "${key}" must not contain a colon`);
    }
    if (typeof value !== 'string') {
      error(
        'R5.3',
        valuePath,
        `every attribute value must be a string; this is ${Array.isArray(value) ? 'an array' : typeof value}`,
      );
      continue;
    }

    const term = lookupTerm(key);
    const looksLikeList = value.startsWith('[');
    if (term?.many !== true && !looksLikeList) continue;

    const parsed = parseJsonArray(value);
    if (parsed === undefined) {
      const rule = term?.many === true ? 'R5.4' : 'R9.2';
      const report = term?.many === true ? error : warn;
      report(rule, valuePath, `must hold a JSON-encoded array of strings; got ${value.slice(0, 40)}`);
      continue;
    }
    if (parsed.some((item) => typeof item !== 'string')) {
      error('R5.4', valuePath, 'the encoded array must contain only strings');
    }
  }
}

/** R6.1–R6.5 */
function checkRelatesTo(relatesTo: unknown, path: string, context: NodeContext): void {
  const { error, warn } = context;
  if (relatesTo === undefined) return;

  if (!Array.isArray(relatesTo)) {
    error('R6.1', path, 'must be a native JSON array, never a stringified one');
    return;
  }
  if (relatesTo.length === 0) {
    warn('R6.4', path, 'omit relatesTo rather than emitting an empty array');
    return;
  }
  const seen = new Set<string>();
  for (let index = 0; index < relatesTo.length; index += 1) {
    const entry = relatesTo[index] as unknown;
    const entryPath = `${path}[${String(index)}]`;
    if (typeof entry !== 'string') {
      error('R6.1', entryPath, 'every entry must be a CURIE string');
      continue;
    }
    if (seen.has(entry)) warn('R6.5', entryPath, `"${entry}" appears more than once`);
    seen.add(entry);
  }
}

/**
 * Every CURIE the node points at: `relatesTo`, `attributes.dpemType`, and the attributes whose
 * term is a reference in the ontology. Nothing else — see the comment at the call site.
 */
function collectReferences(
  node: Record<string, unknown>,
  path: string,
): readonly [string, string][] {
  const found: [string, string][] = [];

  const relatesTo = node.relatesTo;
  if (Array.isArray(relatesTo)) {
    for (let index = 0; index < relatesTo.length; index += 1) {
      const entry = relatesTo[index] as unknown;
      if (typeof entry === 'string') found.push([entry, `${path}.relatesTo[${String(index)}]`]);
    }
  }

  const attributes = node.attributes;
  if (!isPlainObject(attributes)) return found;

  for (const [key, value] of Object.entries(attributes)) {
    if (typeof value !== 'string') continue;
    // `dpemType` is deliberately absent: it names a *class*, so its prefix is checked (in
    // `checkAttributes`) but it resolves to no node, and counting it here would report every
    // node's own type as a dangling reference.
    if (!REFERENCE_TERM_NAMES.has(key)) continue;
    for (const reference of parseJsonArray(value) ?? [value]) {
      if (typeof reference === 'string') found.push([reference, `${path}.attributes.${key}`]);
    }
  }

  return found;
}

/** The encoded value of a list attribute, or `undefined` when it is not one. */
function parseJsonArray(value: string): readonly unknown[] | undefined {
  if (!value.startsWith('[')) return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function interopComplianceReport(document: unknown): ComplianceReport {
  const violations = checkInteropCompliance(document);
  const errorCount = violations.filter((violation) => violation.severity === 'error').length;
  return {
    violations,
    errorCount,
    warningCount: violations.length - errorCount,
    compliant: errorCount === 0,
  };
}

/** True when no rule the processor enforces is broken. Warnings do not count. */
export function isInteropCompliant(document: unknown): boolean {
  return interopComplianceReport(document).compliant;
}

export function formatComplianceViolation(violation: ComplianceViolation): string {
  const where = violation.path.length === 0 ? '' : ` [${violation.path}]`;
  return `${violation.rule} ${violation.severity}${where}: ${violation.message}`;
}
